import PropTypes from "prop-types";
import { Stage, Layer, Circle, Image, Group, Text, Line } from "react-konva";
import useImage from "use-image";
import { useRef } from "react";

import { Spin } from "antd";
import { Body } from "@vds/typography";
import { getLinePointsFromZoneCoordinates, getZoneCentroidCircles } from "../../../global/utility/floormap";
import { generateRandomString } from "../../../global/utility/random";

function AssetFloorMap(props) {
  const { floor, setting, onZoomIn, onZoomOut, onDrag } = props;
  
  const containerRef = useRef(null);
  const stageRef = useRef(null);

  const [image] = useImage(floor?.imageUrl);

  const handleDrag = (state, event) => {
    const stageContainer = stageRef.current?.container();
    if (!stageContainer) return;
    // Change cursor style to indicate dragging

    stageContainer.style.cursor = state === "start" ? "grabbing" : "default";

    if (state === "start") {
      onDrag(true);
    } else if (state === "end") {
      onDrag(false);
    }
  };

  const handleMouseWheel = (e) => {
    e.evt.preventDefault();
    if (e.evt.deltaY > 0) {
      onZoomOut();
    } else {
      onZoomIn();
    }
  };
  
  return (
    <div
      ref={containerRef}
      className="ats-floor-img-view"
      style={{ height: setting.height, position: "relative" }}
    >
      {floor?.floorImageId && floor?.imageUrl !== null && (
        <Spin spinning={setting.isLoading} tip="Loading...">
          <Stage
            ref={stageRef}
            width={800}
            height={setting.height !== "450px" ? 650 : 448}
            scaleX={setting.scale}
            scaleY={setting.scale}
            x={setting.translateX}
            y={setting.translateY}
            draggable
            onDragStart={(e) => handleDrag("start", e)}
            onDragEnd={(e) => handleDrag("end", e)}
            onWheel={handleMouseWheel}
          >
            <Layer imageSmoothingEnabled={false}>
              <Image image={image} name="floorState" width={600} height={360} />
              {!setting.isLoading && Array.isArray(floor?.zones) && floor?.zones?.length > 0 &&
                getLinePointsFromZoneCoordinates(floor?.zones)
                .filter((zone) => zone?.coordinates?.length > 0)
                .map((zone) => (
                  <Line
                    key={`${zone.id || generateRandomString(5)}-line`}
                    points={
                      zone?.coordinates &&
                      Array.isArray(zone?.coordinates) &&
                      zone?.coordinates?.length > 0
                        ? zone?.coordinates
                        : []
                    }
                    fill="rgba(0, 137, 236, 0.1)"
                    stroke="rgba(0, 137, 236, 1)"
                    strokeWidth={2}
                    lineCap="round"
                    lineJoin="round"
                    dash={[5, 5]}
                    closed
                  />
                ))}
              {!setting.isLoading && Array.isArray(floor?.zones) && floor?.zones?.length > 0 &&
                getZoneCentroidCircles(floor?.zones)
                  .filter((zone) => zone?.centroid?.x !== null && zone?.centroid?.y !== null)
                  .map((zone, index) => (
                    <Group
                      name={zone?.name}
                      x={zone?.centroid?.x}
                      y={zone?.centroid?.y}
                      id={zone?.id}
                      key={zone?.id + "-group"}
                    >
                      <Circle
                        id={zone?.id + "-circle"}
                        name={zone?.id}
                        radius={10}
                        fill="#00B140"
                        stroke="#FFFFFF"
                        strokeWidth={2}
                        shadowBlur={12}
                        shadowColor="rgba(0, 0, 0, 0.2)"
                        shadowOffsetX={0}
                        shadowOffsetY={0}
                      />
                      <Text
                        id={zone?.id + "-text-1" + index}
                        text={
                          zone?.deviceCount ? zone?.deviceCount.toString() : "0"
                        }
                        fontFamily="Verizon-NHG-eDS"
                        fontSize={11}
                        fill="#FFFFFF"
                        align="center"
                        verticalAlign="middle"
                        offsetX={3.5}
                        offsetY={5}
                        listening={false}
                      />
                      <Text
                        id={zone?.id + "-text-label" + index}
                        text={zone?.zone_name ? zone?.zone_name.toString() : ""}
                        fontStyle="bold"
                        fontFamily="Verizon-NHG-eDS"
                        fontSize={9}
                        fill="rgba(0, 111, 193, 1)"
                        align="center"
                        verticalAlign="middle"
                        offsetX={25}
                        offsetY={-15}
                        listening={false}
                      />
                    </Group>
                  ))}
            </Layer>
          </Stage>
          <div className="ats-floor-zoom">
            <button onClick={onZoomIn} className="ats-floor-zoom-btn">
              <img src="/assets/zoom-in.png" alt="Zoom In" />
            </button>
            <button onClick={onZoomOut} className="ats-floor-zoom-btn">
              <img src="/assets/zoom-out.png" alt="Zoom Out" />
            </button>
          </div>
        </Spin>
      )}
      {floor?.imageUrl === null && !floor.isLoading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ width: "100%", height: setting.height }}
        >
          <Body size="medium" bold={false}>
            No floor image available
          </Body>
        </div>
      )}
    </div>
  );
}

AssetFloorMap.propTypes = {
  floor: PropTypes.object.isRequired,
  setting: PropTypes.object.isRequired,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  onDrag: PropTypes.func.isRequired,
};

export default AssetFloorMap;
